import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiPlus,
  FiDollarSign,
  FiCheckCircle,
  FiAlertTriangle,
  FiClock,
  FiBarChart2,
  FiCalendar,
  FiCreditCard,
  FiFileText,
  FiX,
  FiFilter,
  FiTrendingUp,
  FiArchive,
  FiEdit,
  FiMoreVertical,
} from 'react-icons/fi';
import { FaBuilding, FaMoneyCheckAlt, FaUniversity, FaMobileAlt, FaReceipt } from 'react-icons/fa';
import { MdOutlinePayment, MdHistory } from 'react-icons/md';

const Payments = () => {
  const navigate = useNavigate();
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [showPaymentHistory, setShowPaymentHistory] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [payments, setPayments] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [timeRange, setTimeRange] = useState('all');
  const [paymentHistory, setPaymentHistory] = useState([]);

  // New payment form state
  const [newPayment, setNewPayment] = useState({
    vendor: '',
    invoiceNumber: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentAmount: '',
    paymentMethod: '',
    referenceNumber: '',
    notes: '',
  });

  // Sample data - in real app, this would come from APIs
  useEffect(() => {
    // Sample vendors
    setVendors([
      { id: 1, name: 'ABC Cleaning Supplies', category: 'Cleaning' },
      { id: 2, name: 'Hotel Linens Inc', category: 'Linens' },
      { id: 3, name: 'Kitchen Equipment Co', category: 'Kitchen' },
      { id: 4, name: 'Office Supplies Pro', category: 'Office' },
      { id: 5, name: 'Beverage Suppliers Ltd', category: 'Beverages' },
    ]);

    // Sample purchase orders for dropdown
    setPurchaseOrders([
      {
        id: 'PO-2024-001',
        vendor: 'ABC Cleaning Supplies',
        total: 259.85,
        dueAmount: 259.85,
        dueDate: '2024-02-15',
      },
      {
        id: 'PO-2024-002',
        vendor: 'Hotel Linens Inc',
        total: 2050.0,
        dueAmount: 2050.0,
        dueDate: '2024-02-20',
      },
      {
        id: 'PO-2024-003',
        vendor: 'Kitchen Equipment Co',
        total: 787.5,
        dueAmount: 787.5,
        dueDate: '2024-02-10',
      },
      {
        id: 'PO-2024-004',
        vendor: 'Office Supplies Pro',
        total: 295.0,
        dueAmount: 295.0,
        dueDate: '2024-02-28',
      },
      {
        id: 'PO-2024-005',
        vendor: 'Beverage Suppliers Ltd',
        total: 1200.0,
        dueAmount: 1200.0,
        dueDate: '2024-02-25',
      },
    ]);

    // Sample payments data
    const samplePayments = [
      {
        id: 1,
        vendor: 'ABC Cleaning Supplies',
        invoiceNumber: 'PO-2024-001',
        dueDate: '2024-02-15',
        totalAmount: 259.85,
        amountPaid: 259.85,
        paymentStatus: 'paid',
        paymentDate: '2024-01-25',
        paymentMethod: 'Bank Transfer',
        referenceNumber: 'BT-001234',
        category: 'Cleaning',
      },
      {
        id: 2,
        vendor: 'Hotel Linens Inc',
        invoiceNumber: 'PO-2024-002',
        dueDate: '2024-02-20',
        totalAmount: 2050.0,
        amountPaid: 1000.0,
        paymentStatus: 'partial',
        paymentDate: '2024-01-26',
        paymentMethod: 'Cheque',
        referenceNumber: 'CHQ-567890',
        category: 'Linens',
      },
      {
        id: 3,
        vendor: 'Kitchen Equipment Co',
        invoiceNumber: 'PO-2024-003',
        dueDate: '2024-02-10',
        totalAmount: 787.5,
        amountPaid: 0,
        paymentStatus: 'overdue',
        paymentDate: '',
        paymentMethod: '',
        referenceNumber: '',
        category: 'Kitchen',
      },
      {
        id: 4,
        vendor: 'Office Supplies Pro',
        invoiceNumber: 'PO-2024-004',
        dueDate: '2024-02-28',
        totalAmount: 295.0,
        amountPaid: 0,
        paymentStatus: 'unpaid',
        paymentDate: '',
        paymentMethod: '',
        referenceNumber: '',
        category: 'Office',
      },
      {
        id: 5,
        vendor: 'Beverage Suppliers Ltd',
        invoiceNumber: 'PO-2024-005',
        dueDate: '2024-02-25',
        totalAmount: 1200.0,
        amountPaid: 600.0,
        paymentStatus: 'partial',
        paymentDate: '2024-01-28',
        paymentMethod: 'UPI',
        referenceNumber: 'UPI-987654',
        category: 'Beverages',
      },
    ];

    setPayments(samplePayments);

    // Sample payment history
    setPaymentHistory([
      {
        id: 1,
        invoiceNumber: 'PO-2024-002',
        date: '2024-01-20',
        amount: 500.0,
        method: 'Bank Transfer',
      },
      { id: 2, invoiceNumber: 'PO-2024-002', date: '2024-01-26', amount: 500.0, method: 'Cheque' },
      { id: 3, invoiceNumber: 'PO-2024-005', date: '2024-01-28', amount: 600.0, method: 'UPI' },
    ]);
  }, []);

  // Enhanced stats calculations
  const stats = {
    totalPayable: payments.reduce((sum, payment) => sum + payment.totalAmount, 0),
    totalPaid: payments.reduce((sum, payment) => sum + payment.amountPaid, 0),
    overduePayments: payments
      .filter((payment) => payment.paymentStatus === 'overdue')
      .reduce((sum, payment) => sum + (payment.totalAmount - payment.amountPaid), 0),
    paymentsThisMonth: payments
      .filter(
        (payment) => payment.paymentStatus === 'paid' && payment.paymentDate.includes('2024-01')
      )
      .reduce((sum, payment) => sum + payment.amountPaid, 0),
    pendingPayments: payments
      .filter(
        (payment) => payment.paymentStatus === 'unpaid' || payment.paymentStatus === 'partial'
      )
      .reduce((sum, payment) => sum + (payment.totalAmount - payment.amountPaid), 0),
  };

  // Filter payments based on search and filters
  const filteredPayments = payments.filter((payment) => {
    const matchesSearch =
      payment.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      payment.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === 'all' || payment.paymentStatus === selectedStatus;
    const matchesVendor = selectedVendor === 'all' || payment.vendor === selectedVendor;

    return matchesSearch && matchesStatus && matchesVendor;
  });

  // Status badge component with React Icons
  const PaymentStatusBadge = ({ status }) => {
    const statusConfig = {
      paid: {
        color: 'bg-green-100 text-green-800 border-green-200',
        text: 'Paid',
        icon: <FiCheckCircle className="w-3 h-3" />,
      },
      partial: {
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        text: 'Partial',
        icon: <FiTrendingUp className="w-3 h-3" />,
      },
      unpaid: {
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        text: 'Unpaid',
        icon: <FiClock className="w-3 h-3" />,
      },
      overdue: {
        color: 'bg-red-100 text-red-800 border-red-200',
        text: 'Overdue',
        icon: <FiAlertTriangle className="w-3 h-3" />,
      },
    };

    const config = statusConfig[status] || statusConfig.unpaid;

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium border ${config.color} flex items-center gap-1`}
      >
        {config.icon}
        {config.text}
      </span>
    );
  };

  // Payment method badge with React Icons
  const PaymentMethodBadge = ({ method }) => {
    const methodConfig = {
      'Bank Transfer': {
        color: 'bg-blue-100 text-blue-800',
        icon: <FaUniversity className="w-3 h-3" />,
      },
      Cheque: {
        color: 'bg-purple-100 text-purple-800',
        icon: <FaMoneyCheckAlt className="w-3 h-3" />,
      },
      Cash: {
        color: 'bg-green-100 text-green-800',
        icon: <FiDollarSign className="w-3 h-3" />,
      },
      UPI: {
        color: 'bg-orange-100 text-orange-800',
        icon: <FaMobileAlt className="w-3 h-3" />,
      },
    };

    const config = methodConfig[method] || {
      color: 'bg-gray-100 text-gray-800',
      icon: <FiCreditCard className="w-3 h-3" />,
    };

    return (
      <span
        className={`px-2 py-1 rounded text-xs font-medium ${config.color} flex items-center gap-1`}
      >
        {config.icon}
        {method}
      </span>
    );
  };

  // Handle vendor change in payment form
  const handleVendorChange = (vendorName) => {
    setNewPayment({
      ...newPayment,
      vendor: vendorName,
      invoiceNumber: '', // Reset invoice when vendor changes
    });
  };

  // Handle invoice selection - auto-fill amount
  const handleInvoiceChange = (invoiceNumber) => {
    const selectedPO = purchaseOrders.find((po) => po.id === invoiceNumber);
    setNewPayment({
      ...newPayment,
      invoiceNumber: invoiceNumber,
      paymentAmount: selectedPO ? selectedPO.dueAmount : '',
    });
  };

  // Process new payment
  const processPayment = (e) => {
    e.preventDefault();

    const updatedPayments = payments.map((payment) => {
      if (payment.invoiceNumber === newPayment.invoiceNumber) {
        const newAmountPaid = payment.amountPaid + parseFloat(newPayment.paymentAmount);
        let newStatus = 'partial';

        if (newAmountPaid >= payment.totalAmount) {
          newStatus = 'paid';
        } else if (new Date(payment.dueDate) < new Date()) {
          newStatus = 'overdue';
        }

        return {
          ...payment,
          amountPaid: newAmountPaid,
          paymentStatus: newStatus,
          paymentDate: newPayment.paymentDate,
          paymentMethod: newPayment.paymentMethod,
          referenceNumber: newPayment.referenceNumber,
        };
      }
      return payment;
    });

    // Add to payment history
    const historyEntry = {
      id: paymentHistory.length + 1,
      invoiceNumber: newPayment.invoiceNumber,
      date: newPayment.paymentDate,
      amount: parseFloat(newPayment.paymentAmount),
      method: newPayment.paymentMethod,
      reference: newPayment.referenceNumber,
    };
    setPaymentHistory([historyEntry, ...paymentHistory]);

    setPayments(updatedPayments);
    setShowProcessModal(false);
    setNewPayment({
      vendor: '',
      invoiceNumber: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentAmount: '',
      paymentMethod: '',
      referenceNumber: '',
      notes: '',
    });
  };

  // Get invoices for selected vendor
  const getVendorInvoices = () => {
    if (!newPayment.vendor) return [];
    return purchaseOrders.filter(
      (po) =>
        po.vendor === newPayment.vendor &&
        payments.find((p) => p.invoiceNumber === po.id && p.paymentStatus !== 'paid')
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  // Calculate days until due
  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get due date badge
  const DueDateBadge = ({ dueDate, status }) => {
    if (status === 'paid') return null;

    const days = getDaysUntilDue(dueDate);

    if (days < 0) {
      return (
        <span className="text-red-600 text-sm font-medium flex items-center gap-1">
          <FiAlertTriangle className="w-3 h-3" />
          Overdue by {Math.abs(days)} days
        </span>
      );
    } else if (days <= 7) {
      return (
        <span className="text-orange-600 text-sm font-medium flex items-center gap-1">
          <FiClock className="w-3 h-3" />
          Due in {days} days
        </span>
      );
    } else {
      return (
        <span className="text-gray-600 text-sm flex items-center gap-1">
          <FiCalendar className="w-3 h-3" />
          Due in {days} days
        </span>
      );
    }
  };

  // Vendor category breakdown
  const vendorBreakdown = vendors
    .map((vendor) => {
      const vendorPayments = payments.filter((p) => p.vendor === vendor.name);
      const totalAmount = vendorPayments.reduce((sum, p) => sum + p.totalAmount, 0);
      const amountPaid = vendorPayments.reduce((sum, p) => sum + p.amountPaid, 0);

      return {
        vendor: vendor.name,
        category: vendor.category,
        totalAmount,
        amountPaid,
        pending: totalAmount - amountPaid,
      };
    })
    .filter((v) => v.totalAmount > 0);

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      {/* Header */}
      <header className="flex justify-between items-center mb-6 bg-white p-3">
        <h1 className="text-black text-xl font-bold">Vendor Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/mdm/vendor-management')}
            className="text-gray-600 hover:text-black transition-colors"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/vendor-management/accounts')}
            className="text-gray-600 hover:text-black transition-colors"
          >
            Vendor Accounts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/vendor-management/purchase-orders')}
            className="text-gray-600 hover:text-black transition-colors"
          >
            Purchase Orders
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/vendor-management/payments')}
            className="text-black font-bold"
          >
            Payments
          </button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Page Title and Actions */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Payments Dashboard</h1>
            <p className="text-gray-600">
              Manage vendor payments, track payables, and monitor cash flow
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowPaymentHistory(true)}
              className="bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 font-medium flex items-center gap-2 transition-colors focus:outline-none"
            >
              <MdHistory className="w-4 h-4" />
              Payment History
            </button>
            <button
              onClick={() => setShowProcessModal(true)}
              className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 font-medium flex items-center gap-2 transition-colors focus:outline-none"
            >
              <MdOutlinePayment className="w-4 h-4" />
              Process Payment
            </button>
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(stats.totalPayable)}
                </div>
                <div className="text-gray-600 text-sm">Total Payable</div>
              </div>
              <div className="text-2xl text-gray-400">
                <FiBarChart2 className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">Across {payments.length} invoices</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(stats.totalPaid)}
                </div>
                <div className="text-gray-600 text-sm">Total Paid</div>
              </div>
              <div className="text-2xl text-green-400">
                <FiCheckCircle className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              {((stats.totalPaid / stats.totalPayable) * 100).toFixed(1)}% completed
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-red-600">
                  {formatCurrency(stats.overduePayments)}
                </div>
                <div className="text-gray-600 text-sm">Overdue</div>
              </div>
              <div className="text-2xl text-red-400">
                <FiAlertTriangle className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">Immediate attention required</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {formatCurrency(stats.paymentsThisMonth)}
                </div>
                <div className="text-gray-600 text-sm">This Month</div>
              </div>
              <div className="text-2xl text-blue-400">
                <FiCalendar className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">January payments</div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-orange-600">
                  {formatCurrency(stats.pendingPayments)}
                </div>
                <div className="text-gray-600 text-sm">Pending</div>
              </div>
              <div className="text-2xl text-orange-400">
                <FiClock className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-2 text-xs text-gray-500">Upcoming payments</div>
          </div>
        </div>

        {/* Vendor Breakdown */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FaBuilding className="w-4 h-4" />
              Vendor Breakdown
            </h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {vendorBreakdown.map((vendor, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-gray-900">{vendor.vendor}</h3>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                      {vendor.category}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Total:</span>
                      <span className="font-medium">{formatCurrency(vendor.totalAmount)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Paid:</span>
                      <span className="font-medium text-green-600">
                        {formatCurrency(vendor.amountPaid)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Pending:</span>
                      <span className="font-medium text-orange-600">
                        {formatCurrency(vendor.pending)}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${(vendor.amountPaid / vendor.totalAmount) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced Filters */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
              <div className="flex-1 relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by vendor, invoice number, or reference..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <div className="relative">
                  <FiFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <select
                    className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-400"
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                  >
                    <option value="all">All Status</option>
                    <option value="paid">Paid</option>
                    <option value="partial">Partial</option>
                    <option value="unpaid">Unpaid</option>
                    <option value="overdue">Overdue</option>
                  </select>
                </div>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-400"
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                >
                  <option value="all">All Vendors</option>
                  {vendors.map((vendor) => (
                    <option key={vendor.id} value={vendor.name}>
                      {vendor.name}
                    </option>
                  ))}
                </select>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-400"
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  <option value="all">All Time</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                </select>
              </div>
            </div>
          </div>

          {/* Enhanced Payments Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPayments.map((payment) => (
                  <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{payment.vendor}</div>
                        <div className="text-xs text-gray-500">{payment.category}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-mono text-gray-900 flex items-center gap-1">
                        <FaReceipt className="w-3 h-3 text-gray-400" />
                        {payment.invoiceNumber}
                      </div>
                      {payment.referenceNumber && (
                        <div className="text-xs text-gray-500">Ref: {payment.referenceNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{payment.dueDate}</div>
                      <DueDateBadge dueDate={payment.dueDate} status={payment.paymentStatus} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(payment.totalAmount)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(payment.amountPaid)}
                      </div>
                      {payment.paymentMethod && (
                        <div className="mt-1">
                          <PaymentMethodBadge method={payment.paymentMethod} />
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <PaymentStatusBadge status={payment.paymentStatus} />
                      {payment.paymentDate && (
                        <div className="text-xs text-gray-500 mt-1">
                          Paid on {payment.paymentDate}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex space-x-2">
                        {payment.paymentStatus !== 'paid' && (
                          <button
                            onClick={() => {
                              setNewPayment({
                                vendor: payment.vendor,
                                invoiceNumber: payment.invoiceNumber,
                                paymentDate: new Date().toISOString().split('T')[0],
                                paymentAmount: (payment.totalAmount - payment.amountPaid).toFixed(
                                  2
                                ),
                                paymentMethod: '',
                                referenceNumber: '',
                                notes: '',
                              });
                              setShowProcessModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 transition-colors focus:outline-none"
                          >
                            <MdOutlinePayment className="w-3 h-3" />
                            Pay Now
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowPaymentHistory(true);
                          }}
                          className="text-gray-600 hover:text-gray-800 text-sm font-medium flex items-center gap-1 transition-colors focus:outline-none"
                        >
                          <MdHistory className="w-3 h-3" />
                          History
                        </button>
                        <button className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none">
                          <FiMoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Empty State */}
          {filteredPayments.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">
                <FiArchive className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No payments found</h3>
              <p className="text-gray-600 mb-4">Try adjusting your search or filters</p>
              <button
                onClick={() => setShowProcessModal(true)}
                className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors focus:outline-none flex items-center gap-2 mx-auto"
              >
                <FiPlus className="w-4 h-4" />
                Create First Payment
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Process Payment Modal */}
      {showProcessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Process Payment</h2>
                  <p className="text-gray-600">Record a new payment to vendor</p>
                </div>
                <button
                  onClick={() => setShowProcessModal(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={processPayment}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Vendor *</label>
                    <select
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
                      value={newPayment.vendor}
                      onChange={(e) => handleVendorChange(e.target.value)}
                    >
                      <option value="">Select Vendor</option>
                      {vendors.map((vendor) => (
                        <option key={vendor.id} value={vendor.name}>
                          {vendor.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Invoice/PO Number *
                    </label>
                    <select
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
                      value={newPayment.invoiceNumber}
                      onChange={(e) => handleInvoiceChange(e.target.value)}
                      disabled={!newPayment.vendor}
                    >
                      <option value="">Select Invoice/PO</option>
                      {getVendorInvoices().map((po) => (
                        <option key={po.id} value={po.id}>
                          {po.id} - {formatCurrency(po.dueAmount)} due
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Date *
                    </label>
                    <input
                      type="date"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
                      value={newPayment.paymentDate}
                      onChange={(e) =>
                        setNewPayment({ ...newPayment, paymentDate: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Amount *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
                      placeholder="Enter payment amount"
                      value={newPayment.paymentAmount}
                      onChange={(e) =>
                        setNewPayment({ ...newPayment, paymentAmount: e.target.value })
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method *
                    </label>
                    <select
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
                      value={newPayment.paymentMethod}
                      onChange={(e) =>
                        setNewPayment({ ...newPayment, paymentMethod: e.target.value })
                      }
                    >
                      <option value="">Select Payment Method</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Cash">Cash</option>
                      <option value="UPI">UPI</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
                      placeholder="Enter reference number (e.g., BT-001234)"
                      value={newPayment.referenceNumber}
                      onChange={(e) =>
                        setNewPayment({ ...newPayment, referenceNumber: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <textarea
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-400"
                    rows="3"
                    placeholder="Add any additional payment notes or comments..."
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })}
                  />
                </div>

                {/* Enhanced Payment Summary */}
                {newPayment.invoiceNumber && (
                  <div className="bg-gray-50 p-4 rounded-lg mb-6 border border-gray-200">
                    <h3 className="font-semibold mb-3 text-gray-900 flex items-center gap-2">
                      <FiFileText className="w-4 h-4" />
                      Payment Summary
                    </h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="text-gray-600">Invoice:</div>
                      <div className="font-medium">{newPayment.invoiceNumber}</div>
                      <div className="text-gray-600">Vendor:</div>
                      <div className="font-medium">{newPayment.vendor}</div>
                      <div className="text-gray-600">Payment Date:</div>
                      <div className="font-medium">{newPayment.paymentDate}</div>
                      <div className="text-gray-600">Amount:</div>
                      <div className="font-medium text-green-600 text-lg">
                        {formatCurrency(newPayment.paymentAmount)}
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowProcessModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 font-medium transition-colors focus:outline-none flex items-center justify-center gap-2"
                  >
                    <FiX className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-black text-white py-3 rounded-lg hover:bg-gray-800 font-medium transition-colors focus:outline-none flex items-center justify-center gap-2"
                  >
                    <MdOutlinePayment className="w-4 h-4" />
                    Process Payment
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showPaymentHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Payment History</h2>
                  <p className="text-gray-600">Complete transaction history</p>
                </div>
                <button
                  onClick={() => setShowPaymentHistory(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors focus:outline-none"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {paymentHistory.map((history) => (
                  <div
                    key={history.id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-gray-900">
                            {history.invoiceNumber}
                          </span>
                          <PaymentMethodBadge method={history.method} />
                        </div>
                        <div className="text-sm text-gray-600">
                          Paid on {history.date} • Reference: {history.reference || 'N/A'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-green-600">
                          {formatCurrency(history.amount)}
                        </div>
                        <div className="text-xs text-gray-500">Completed</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {paymentHistory.length === 0 && (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-2">
                    <FiFileText className="w-12 h-12 mx-auto" />
                  </div>
                  <p className="text-gray-600">No payment history found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
